import {skipToken} from '@reduxjs/toolkit/query';

import {isNil, isNotNil} from 'ramda';

import {useGetTenantV2Query} from '@dms/api/base';
import type {CheckoutProfileRuleWrite} from '@dms/api/sales';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {useGetDirectSaleTypesQuery, useGetDirectSaleVariantsQuery} from '@dms/api/warehouse';
import {
  useGetServiceCaseTypesQuery,
  useGetServiceOrderTypesQuery,
  useGetSettingsServiceOrderVariantsAllQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';

type CheckoutRuleLookupOption = {value: string; label: string};

type CheckoutRuleVariantLookup = CheckoutRuleLookupOption & {
  firstTypeName?: string;
  secondTypeName?: string;
};

type CheckoutRuleLookupGroup = {
  field: keyof CheckoutProfileRuleWrite['conditions'];
  label: string;
  options: CheckoutRuleLookupOption[];
  isLoading: boolean;
  isError: boolean;
};

export function useCheckoutRuleLookups(
  branchId: string,
  sourceType: CheckoutProfileRuleWrite['sourceType']
) {
  const isServiceOrder = sourceType === 'service-order';
  const isDirectSale = sourceType === 'direct-sale';
  const hasBranch = branchId.length > 0;
  const tenant = useGetTenantV2Query(undefined, {skip: !isDirectSale || !hasBranch});
  const authorizationProfiles = useGetAuthorizationProfilesQuery(
    !isDirectSale || !hasBranch || isNil(tenant.data)
      ? skipToken
      : {'x-tenant': tenant.data.id, branchId}
  );

  const serviceCaseTypes = useGetServiceCaseTypesQuery(undefined, {
    skip: !isServiceOrder || !hasBranch,
  });

  const serviceOrderTypes = useGetServiceOrderTypesQuery(undefined, {
    skip: !isServiceOrder || !hasBranch,
  });

  // The rule can target a branch other than the global one, so the branch goes with the request.
  const serviceOrderVariants = useGetSettingsServiceOrderVariantsAllQuery(
    isServiceOrder && hasBranch ? {branchId} : skipToken
  );

  const directSaleTypes = useGetDirectSaleTypesQuery(undefined, {
    skip: !isDirectSale || !hasBranch,
  });

  const directSaleVariants = useGetDirectSaleVariantsQuery(
    isDirectSale && hasBranch ? {branchId} : skipToken
  );

  const variants: CheckoutRuleVariantLookup[] = isServiceOrder
    ? (serviceOrderVariants.currentData ?? []).flatMap((variant) => {
        if (isNil(variant) || isNil(variant.id)) {
          return [];
        }

        return [
          {
            value: variant.id,
            label: variant.name ?? variant.id,
            firstTypeName: serviceCaseTypes.currentData?.find(
              (type) => type.id === variant.serviceCaseTypeId
            )?.name,
            secondTypeName:
              serviceOrderTypes.currentData?.find((type) => type?.id === variant.serviceOrderTypeId)
                ?.name ?? undefined,
          },
        ];
      })
    : (directSaleVariants.currentData ?? [])
        .filter((variant) => variant.isActive)
        .map((variant) => ({value: variant.directSaleVariantId, label: variant.name}));

  const groups: CheckoutRuleLookupGroup[] = isServiceOrder
    ? [
        {
          field: 'serviceCaseTypeIds',
          label: i18n.t('entity.checkout.labels.serviceCaseVariants'),
          options: (serviceCaseTypes.currentData ?? []).map((type) => ({
            value: type.id,
            label: type.name,
          })),
          isLoading: serviceCaseTypes.isFetching,
          isError: serviceCaseTypes.isError,
        },
        {
          field: 'serviceOrderTypeIds',
          label: i18n.t('entity.settings.labels.serviceOrderTypes'),
          options: (serviceOrderTypes.currentData ?? []).flatMap((type) =>
            isNotNil(type) && isNotNil(type.id)
              ? [{value: type.id, label: type.name ?? type.id}]
              : []
          ),
          isLoading: serviceOrderTypes.isFetching,
          isError: serviceOrderTypes.isError,
        },
        {
          field: 'serviceOrderVariantIds',
          label: i18n.t('entity.order.labels.serviceOrderVariants'),
          options: variants,
          isLoading: serviceOrderVariants.isFetching,
          isError: serviceOrderVariants.isError,
        },
      ]
    : [
        {
          field: 'authorizationProfileIds',
          label: i18n.t('entity.checkout.labels.directSaleAuthorizationProfiles'),
          options: (authorizationProfiles.currentData ?? [])
            .filter((profile) => profile.branchId === branchId)
            .map((profile) => ({value: profile.id, label: profile.name})),
          isLoading: tenant.isFetching || authorizationProfiles.isFetching,
          isError: tenant.isError || authorizationProfiles.isError,
        },
        {
          field: 'directSaleTypeIds',
          label: i18n.t('entity.checkout.labels.directSaleTypes'),
          options: (directSaleTypes.currentData ?? []).map((type) => ({
            value: type.directSaleTypeId,
            label: type.name,
          })),
          isLoading: directSaleTypes.isFetching,
          isError: directSaleTypes.isError,
        },
        {
          field: 'directSaleVariantIds',
          label: i18n.t('entity.warehouse.labels.directSaleVariants'),
          options: variants,
          isLoading: directSaleVariants.isFetching,
          isError: directSaleVariants.isError,
        },
      ];

  return {
    groups,
    variants,
    isError: groups.some((group) => group.isError),
  };
}
